import React from 'react';
import { ApolloError } from '@apollo/client';

import { useEndTrialEarlyMutation } from '$/GraphQL/schema/schema';

import Text from '$/components/ui/Text';
import Button from '$/components/ui/Button';

import { logger } from '$/utils/logger';
import SimpleModal from '$/components/ui/SimpleModal';
import { useOnSubscriptionChanged } from '$/hooks/useOnSubscriptionChanged';
import { useStripe } from '@stripe/react-stripe-js';
import { usePlanPageContext } from './PlanPage';
import { formatNumber } from '$/utils/number';

interface EndTrialEarlyModalProps {
  toIncreaseRegistrantLimit?: boolean;
  toggleModal: () => void;
}

const Highlight: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <span css={{ fontWeight: 'bold' }}>{children}</span>
);

export const EndTrialEarlyModal: React.FC<EndTrialEarlyModalProps> = ({
  toggleModal,
  toIncreaseRegistrantLimit,
}) => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const { subscriptionInfo, team } = usePlanPageContext();

  const stripe = useStripe();
  const onSubscriptionChange = useOnSubscriptionChanged();
  const [endTrialEarly] = useEndTrialEarlyMutation();

  const handleEndTrialEarly = async (): Promise<void> => {
    setLoading(true);
    try {
      setErrorMessage('');
      const { data } = await endTrialEarly();
      const paymentIntentSecret = data?.endTrialEarly?.paymentIntentSecret;
      if (paymentIntentSecret) {
        const paymentIntent = await stripe?.confirmCardPayment(paymentIntentSecret);
        if (paymentIntent?.paymentIntent?.status === 'succeeded') {
          // If payment is successful, we need to call endTrialEarly again to update the team status
          await endTrialEarly();
        } else {
          // If payment is not successful, no need to show the error message
          // The subscription will be past due and the plan page will show the error message after onSubscriptionChange is called
        }
      } else {
        // No need to confirm payment if paymentIntentSecret is not returned
      }
      // Call the subscription change hook to team subscription status
      await onSubscriptionChange();
      toggleModal();
    } catch (e) {
      const error = e as ApolloError;

      logger.error('End trial early error', { error });

      setErrorMessage(
        error.graphQLErrors && error.graphQLErrors.length > 0
          ? error.graphQLErrors[0].message
          : error.message
      );
    } finally {
      setLoading(false);
    }
  };

  const message = React.useMemo(() => {
    if (toIncreaseRegistrantLimit) {
      return (
        <Text.body className='text-normal'>
          <p>
            You are allowed <Highlight>up to {team.registrantLimit.limit} registrants</Highlight>{' '}
            during your free trial, which is set to end on{' '}
            {subscriptionInfo.trialEndingDate?.format('P')}. You currently have used{' '}
            <Highlight>
              {formatNumber(100 - team.registrantLimit.registrantsLeftPercent)}%
            </Highlight>{' '}
            of them.
          </p>
          <p>
            If you end your free trial now, your {subscriptionInfo.billingCyclely} subscription will
            begin today at <Highlight>{subscriptionInfo.nextPriceTier.name}</Highlight> – which lets
            you have{' '}
            <Highlight>
              up to {subscriptionInfo.nextPriceTier.registrantLimit.toLocaleString()} registrants
              per month
            </Highlight>{' '}
            – your <Highlight>Registrants</Highlight> will be reset to 0, and your credit credit
            card will be charged <Highlight>{subscriptionInfo.nextPriceText}</Highlight> for the
            first {subscriptionInfo.billingCycleUnit} of your subscription.
          </p>
          <p>Do you wish to end your free trial now and increase your registrant limit?</p>
        </Text.body>
      );
    }
    return (
      <Text.body className='text-normal'>
        <p>
          If you end your free trial now, your {subscriptionInfo.billingCyclely} subscription will
          begin today at <Highlight>{subscriptionInfo.nextPriceTier.name}</Highlight> and your
          credit credit card will be charged <Highlight>{subscriptionInfo.nextPriceText}</Highlight>{' '}
          for the first {subscriptionInfo.billingCycleUnit} of your subscription.
        </p>
        <p>Do you wish to end your free trial now?</p>
      </Text.body>
    );
  }, [
    toIncreaseRegistrantLimit,
    subscriptionInfo.billingCycleUnit,
    subscriptionInfo.billingCyclely,
    subscriptionInfo.nextPriceText,
    subscriptionInfo.nextPriceTier.name,
    subscriptionInfo.nextPriceTier.registrantLimit,
    subscriptionInfo.trialEndingDate,
    team.registrantLimit.limit,
    team.registrantLimit.registrantsLeftPercent,
  ]);

  return (
    <>
      <SimpleModal
        title={
          toIncreaseRegistrantLimit
            ? 'End free trial and increase registrant limit?'
            : 'End free trial early?'
        }
        onCancel={toggleModal}
        closeOnClickOutside={false}
        cancel={<Button>Cancel</Button>}
        submit={
          <Button.submit
            className='bg-color-delete'
            submitting={loading}
            onClick={handleEndTrialEarly}
          >
            {toIncreaseRegistrantLimit
              ? 'End trial and increase registrant limit'
              : 'End trial now'}
          </Button.submit>
        }
      >
        {message}
        {errorMessage && <div className='text-coral-1'>{errorMessage}</div>}
      </SimpleModal>
    </>
  );
};
